import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Home, Copy, Check, Users, Wifi, WifiOff, Share2, BookOpen, ChevronRight, RotateCcw, Trophy, Crown, Star, Loader2 } from 'lucide-react';
import socketService from '../services/socket';

const OnlineGamePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Connection state
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  
  // Room state
  const [playerName, setPlayerName] = useState('');
  const [roomCode, setRoomCode] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [isHost, setIsHost] = useState(false);
  const [opponentName, setOpponentName] = useState('');
  const [roomStatus, setRoomStatus] = useState('idle'); // idle, waiting, ready, playing, finished
  
  // Game state
  const [currentPlayer, setCurrentPlayer] = useState(1);
  const [player1Score, setPlayer1Score] = useState(0);
  const [player2Score, setPlayer2Score] = useState(0);
  const [player1Balls, setPlayer1Balls] = useState([]);
  const [player2Balls, setPlayer2Balls] = useState([]);
  const [currentPage, setCurrentPage] = useState(null);
  const [lastDigit, setLastDigit] = useState(null);
  const [isPlayerOut, setIsPlayerOut] = useState(false);
  const [winner, setWinner] = useState(null);
  const [isFlipping, setIsFlipping] = useState(false);
  
  // UI state
  const [copied, setCopied] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);

  // Check for room code in URL
  useEffect(() => {
    const codeFromUrl = searchParams.get('room');
    if (codeFromUrl) {
      setJoinCode(codeFromUrl.toUpperCase());
    }
  }, [searchParams]);

  // Setup socket event listeners
  const setupSocketListeners = useCallback(() => {
    // Guest joined (for host)
    socketService.on('guest_joined', (data) => {
      setOpponentName(data.guest_name);
      setRoomStatus('ready');
    });

    // Guest left
    socketService.on('guest_left', (data) => {
      setOpponentName('');
      setRoomStatus('waiting');
      setError(data.message);
    });

    // Host left
    socketService.on('host_left', (data) => {
      setError(data.message);
      setRoomStatus('idle');
      setRoomCode('');
    });

    // Game started
    socketService.on('game_started', (data) => {
      setRoomStatus('playing');
      setCurrentPlayer(data.current_player);
      setPlayer1Score(0);
      setPlayer2Score(0);
      setPlayer1Balls([]);
      setPlayer2Balls([]);
      setCurrentPage(null);
      setLastDigit(null);
      setIsPlayerOut(false);
      setWinner(null);
    });

    // Flip result
    socketService.on('flip_result', (data) => {
      setCurrentPage(data.page);
      setLastDigit(data.digit);
      setIsPlayerOut(data.is_out);
      setPlayer1Score(data.player1_score);
      setPlayer2Score(data.player2_score);
      setPlayer1Balls(data.player1_balls);
      setPlayer2Balls(data.player2_balls);
      setIsFlipping(false);
      
      if (data.status === 'finished') {
        setRoomStatus('finished');
        setWinner(data.winner);
      }
    });

    // Player switched
    socketService.on('player_switched', (data) => {
      setCurrentPlayer(data.current_player);
      setCurrentPage(null);
      setLastDigit(null);
      setIsPlayerOut(false);
    });

    // Error
    socketService.on('error', (data) => {
      setError(data.message);
      setIsFlipping(false);
    });
  }, []);

  // Connect to socket on mount
  useEffect(() => {
    const connectSocket = async () => {
      setIsConnecting(true);
      try {
        await socketService.connect();
        setIsConnected(true);
        setupSocketListeners();
      } catch (err) {
        setError('Unable to connect to game server. Online play may not be available in this environment. Try Local PvP instead!');
        console.error('Connection error:', err);
        setIsConnecting(false);
      } finally {
        setIsConnecting(false);
      }
    };

    connectSocket();

    // Set a timeout to show error if connection takes too long
    const timeout = setTimeout(() => {
      if (!socketService.isConnected) {
        setIsConnecting(false);
        setError('Connection timeout. Online play may not be available. Try Local PvP!');
      }
    }, 10000);

    return () => {
      clearTimeout(timeout);
      socketService.leaveRoom();
    };
  }, [setupSocketListeners]);

  const handleCreateRoom = async () => {
    if (!playerName.trim()) {
      setError('Please enter your name');
      return;
    }

    try {
      setError(null);
      const data = await socketService.createRoom(playerName.trim());
      setRoomCode(data.room_code);
      setIsHost(true);
      setRoomStatus('waiting');
      setShowShareDialog(true);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleJoinRoom = async () => {
    if (!playerName.trim()) {
      setError('Please enter your name');
      return;
    }
    if (!joinCode.trim()) {
      setError('Please enter a room code');
      return;
    }

    try {
      setError(null);
      const data = await socketService.joinRoom(joinCode.trim(), playerName.trim());
      setRoomCode(data.room_code);
      setOpponentName(data.host_name);
      setIsHost(false);
      setRoomStatus('ready');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleStartGame = () => {
    socketService.startGame();
  };

  const handleFlip = () => {
    if (isFlipping || isPlayerOut) return;
    
    // Check if it's my turn
    const isMyTurn = (isHost && currentPlayer === 1) || (!isHost && currentPlayer === 2);
    if (!isMyTurn) {
      setError("It's not your turn!");
      return;
    }
    
    setIsFlipping(true);
    setError(null);
    socketService.flipBook();
  };

  const handleNextPlayer = () => {
    socketService.nextPlayer();
  };

  const handleLeaveRoom = () => {
    socketService.leaveRoom();
    setRoomCode('');
    setRoomStatus('idle');
    setOpponentName('');
    setIsHost(false);
  };

  const handlePlayAgain = () => {
    handleLeaveRoom();
  };

  const handleCopyCode = async () => {
    try {
      const shareUrl = `${window.location.origin}/online?room=${roomCode}`;
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/online?room=${roomCode}`;
    const shareData = {
      title: 'Book Cricket - Online Match',
      text: `${playerName} is challenging you to Book Cricket! Join with code: ${roomCode}`,
      url: shareUrl
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        if (err.name !== 'AbortError') {
          setShowShareDialog(true);
        }
      }
    } else {
      setShowShareDialog(true);
    }
  };

  const isMyTurn = (isHost && currentPlayer === 1) || (!isHost && currentPlayer === 2);
  const myName = isHost ? playerName : playerName;
  const myScore = isHost ? player1Score : player2Score;
  const opponentScore = isHost ? player2Score : player1Score;
  const myBalls = isHost ? player1Balls : player2Balls;
  const opponentBalls = isHost ? player2Balls : player1Balls;

  // Winner Screen
  if (roomStatus === 'finished') {
    const iAmWinner = winner === (isHost ? playerName : playerName);
    const isTie = winner === 'Tie';

    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4 relative overflow-hidden">
        {/* Celebration particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 rounded-full animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                backgroundColor: ['#fbbf24', '#f59e0b', '#eab308', '#facc15'][i % 4],
                animationDelay: `${Math.random() * 2}s`,
                opacity: 0.6
              }}
            />
          ))}
        </div>

        <Card className="bg-gradient-to-b from-slate-800 to-slate-900 border-2 border-amber-500/50 max-w-lg w-full shadow-2xl shadow-amber-500/20">
          <CardContent className="p-10 text-center relative">
            <div className="mb-8 relative">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-amber-500/20 rounded-full blur-3xl" />
              
              <div className="relative">
                <div className="w-36 h-36 mx-auto relative">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="relative">
                      {!isTie && (
                        <Crown className="w-10 h-10 text-amber-400 absolute -top-8 left-1/2 -translate-x-1/2 animate-bounce" />
                      )}
                      <div className="w-28 h-28 bg-gradient-to-b from-amber-400 via-amber-500 to-amber-600 rounded-t-full flex items-center justify-center shadow-lg relative">
                        <Trophy className="w-14 h-14 text-amber-900" />
                        <Star className="w-5 h-5 text-yellow-300 absolute -left-3 top-2 animate-pulse fill-yellow-300" />
                        <Star className="w-4 h-4 text-yellow-300 absolute -right-2 top-4 animate-pulse fill-yellow-300" style={{ animationDelay: '0.5s' }} />
                      </div>
                      <div className="w-16 h-4 bg-gradient-to-b from-amber-600 to-amber-700 mx-auto rounded-b" />
                      <div className="w-20 h-3 bg-gradient-to-b from-amber-700 to-amber-800 mx-auto rounded-b" />
                      <div className="w-24 h-2 bg-amber-900 mx-auto rounded-b" />
                    </div>
                  </div>
                </div>
                
                <div className="mt-4 relative">
                  <div className="bg-gradient-to-r from-amber-600 via-amber-500 to-amber-600 px-8 py-3 rounded-lg shadow-lg border-2 border-amber-400 inline-block">
                    <p className="text-amber-950 font-bold text-2xl tracking-wide">
                      {isTie ? "IT'S A TIE!" : winner?.toUpperCase()}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-white mb-2">
              {isTie ? "Both Champions!" : (iAmWinner ? "You Won! 🎉" : "Better Luck Next Time!")}
            </h2>

            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className={`p-5 rounded-xl ${myScore >= opponentScore ? 'bg-gradient-to-br from-amber-500/30 to-amber-600/20 border-2 border-amber-400' : 'bg-slate-700/50 border border-slate-600'}`}>
                <p className="text-slate-300 text-sm mb-1">You ({playerName})</p>
                <p className="text-4xl font-bold text-white">{myScore}</p>
              </div>
              <div className={`p-5 rounded-xl ${opponentScore >= myScore ? 'bg-gradient-to-br from-amber-500/30 to-amber-600/20 border-2 border-amber-400' : 'bg-slate-700/50 border border-slate-600'}`}>
                <p className="text-slate-300 text-sm mb-1">{opponentName}</p>
                <p className="text-4xl font-bold text-white">{opponentScore}</p>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handlePlayAgain}
                className="flex-1 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-bold py-6 text-lg"
              >
                <RotateCcw className="w-5 h-5 mr-2" />
                Play Again
              </Button>
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="border-slate-500 text-slate-300 hover:bg-slate-700/50 py-6"
              >
                Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Playing state
  if (roomStatus === 'playing') {
    const currentPlayerName = currentPlayer === 1 
      ? (isHost ? playerName : opponentName) 
      : (isHost ? opponentName : playerName);
    const currentBalls = currentPlayer === 1 ? player1Balls : player2Balls;

    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-900 via-emerald-800 to-green-900 relative overflow-hidden">
        {/* Stadium Lines */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] border-4 border-white rounded-full" />
        </div>

        <div className="relative z-10 container mx-auto px-4 py-4">
          {/* Header */}
          <div className="flex justify-between items-center mb-4">
            <Button
              variant="ghost"
              onClick={handleLeaveRoom}
              className="text-white hover:bg-white/10"
            >
              <Home className="w-5 h-5 mr-2" />
              Leave
            </Button>
            <Badge variant="outline" className="border-blue-400 text-blue-300">
              <Wifi className="w-3 h-3 mr-1" /> Online • {roomCode}
            </Badge>
          </div>

          {/* Scoreboard */}
          <div className="grid grid-cols-2 gap-4 mb-4 max-w-2xl mx-auto">
            <Card className={`transition-all duration-300 ${currentPlayer === 1 ? 'bg-emerald-700/80 border-amber-400 shadow-lg shadow-amber-400/20' : 'bg-emerald-800/50 border-emerald-600/50'}`}>
              <CardContent className="p-4 text-center">
                <p className="text-emerald-200 text-sm mb-1">{isHost ? `You (${playerName})` : opponentName}</p>
                <p className="text-4xl font-bold text-white">{player1Score}</p>
                {currentPlayer === 2 && player1Balls.length > 0 && (
                  <Badge className="mt-2 bg-red-500/80">OUT</Badge>
                )}
              </CardContent>
            </Card>
            <Card className={`transition-all duration-300 ${currentPlayer === 2 ? 'bg-emerald-700/80 border-amber-400 shadow-lg shadow-amber-400/20' : 'bg-emerald-800/50 border-emerald-600/50'}`}>
              <CardContent className="p-4 text-center">
                <p className="text-emerald-200 text-sm mb-1">{isHost ? opponentName : `You (${playerName})`}</p>
                <p className="text-4xl font-bold text-white">{player2Score}</p>
                {currentPlayer === 1 && (
                  <Badge className="mt-2 bg-emerald-600">Waiting</Badge>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Current Player Indicator */}
          <div className="text-center mb-4">
            <p className="text-emerald-300 text-sm">Now Playing</p>
            <h2 className="text-2xl font-bold text-white">
              {isMyTurn ? "Your Turn!" : `${currentPlayerName}'s Turn`}
            </h2>
            {currentPlayer === 2 && player1Score > 0 && (
              <p className="text-amber-400 text-sm mt-1">Target: {player1Score + 1} runs to win</p>
            )}
          </div>

          {/* Gothic Book */}
          <div className="flex justify-center mb-6">
            <div 
              className={`relative cursor-pointer transition-all duration-500 ${isFlipping ? 'scale-95 rotate-1' : 'hover:scale-105 hover:-rotate-1'} ${!isMyTurn ? 'opacity-60' : ''}`}
              onClick={isMyTurn ? handleFlip : undefined}
            >
              <div className="absolute -inset-4 bg-amber-500/20 rounded-3xl blur-xl opacity-50" />
              
              <div className={`relative w-72 h-96 ${isFlipping ? 'animate-pulse' : ''}`}>
                <div className="absolute -bottom-4 left-4 right-4 h-8 bg-black/40 blur-xl rounded-full" />
                <div className="absolute inset-0 translate-x-2 translate-y-1 bg-gradient-to-br from-stone-800 to-stone-900 rounded-r-lg rounded-l-sm" />
                
                <div className="absolute inset-0 bg-gradient-to-br from-stone-700 via-stone-800 to-stone-900 rounded-r-lg rounded-l-sm shadow-2xl border border-stone-600/50">
                  <div className="absolute top-3 left-3 w-12 h-12 border-l-2 border-t-2 border-amber-600/70 rounded-tl-lg" />
                  <div className="absolute top-3 right-3 w-12 h-12 border-r-2 border-t-2 border-amber-600/70 rounded-tr-lg" />
                  <div className="absolute bottom-3 left-3 w-12 h-12 border-l-2 border-b-2 border-amber-600/70 rounded-bl-lg" />
                  <div className="absolute bottom-3 right-3 w-12 h-12 border-r-2 border-b-2 border-amber-600/70 rounded-br-lg" />
                  
                  <div className="absolute left-0 top-0 bottom-0 w-6 bg-gradient-to-r from-stone-900 via-stone-800 to-transparent rounded-l-sm" />
                  
                  <div className="absolute top-2 right-2 w-6 h-6 bg-gradient-to-br from-amber-500 to-amber-700 rounded-br-lg rounded-tl-sm shadow-md" />
                  <div className="absolute bottom-2 right-2 w-6 h-6 bg-gradient-to-br from-amber-500 to-amber-700 rounded-tr-lg rounded-bl-sm shadow-md" />
                  
                  <div className="absolute inset-10 left-12 bg-gradient-to-br from-amber-50 via-amber-100 to-orange-50 rounded shadow-inner overflow-hidden">
                    {currentPage !== null ? (
                      <div className="h-full flex flex-col items-center justify-center relative z-10 p-4">
                        <p className="text-amber-600/80 text-sm font-serif italic mb-1">Page</p>
                        <p className="text-6xl font-bold text-stone-800 mb-4 font-serif">{currentPage}</p>
                        <div className="w-16 h-0.5 bg-amber-400/50 mb-4" />
                        <div className="flex items-center gap-2 mb-4">
                          <span className="text-stone-600 text-sm font-serif">Last digit:</span>
                          <span className={`text-3xl font-bold font-serif ${isPlayerOut ? 'text-red-600' : 'text-emerald-700'}`}>
                            {lastDigit}
                          </span>
                        </div>
                        {isPlayerOut && (
                          <div className="bg-red-600 text-white px-6 py-2 rounded-full font-bold text-lg animate-pulse shadow-lg">
                            OUT!
                          </div>
                        )}
                        {!isPlayerOut && lastDigit !== null && (
                          <div className="bg-emerald-600 text-white px-6 py-2 rounded-full font-bold text-lg shadow-lg">
                            +{lastDigit} runs!
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="h-full flex flex-col items-center justify-center relative z-10">
                        <div className="w-16 h-16 mb-4 relative">
                          <BookOpen className="w-16 h-16 text-amber-400/80" />
                          {isMyTurn && (
                            <div className="absolute inset-0 animate-ping">
                              <BookOpen className="w-16 h-16 text-amber-400/30" />
                            </div>
                          )}
                        </div>
                        <p className="text-stone-600 text-base text-center px-6 font-serif italic">
                          {isMyTurn ? 'Tap to flip!' : `Waiting for ${currentPlayerName}...`}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  <div className="absolute top-4 left-1/2 -translate-x-1/2 text-amber-600/80 text-xs font-serif tracking-widest">
                    BOOK OF CRICKET
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="max-w-md mx-auto space-y-3">
            {!isPlayerOut && isMyTurn && (
              <Button
                onClick={handleFlip}
                disabled={isFlipping}
                className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-bold py-6 text-lg shadow-lg shadow-amber-500/30 disabled:opacity-50"
              >
                <BookOpen className="w-5 h-5 mr-2" />
                {isFlipping ? 'Flipping...' : 'Flip the Book'}
              </Button>
            )}

            {/* Show "Next Player" button when Player 1 is out - visible to host */}
            {isPlayerOut && currentPlayer === 1 && isHost && (
              <Button
                onClick={handleNextPlayer}
                className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-bold py-6 text-lg"
              >
                <ChevronRight className="w-5 h-5 mr-2" />
                Start {opponentName}'s Turn
              </Button>
            )}

            {/* Show waiting message to guest when Player 1 is out */}
            {isPlayerOut && currentPlayer === 1 && !isHost && (
              <div className="text-center text-amber-300 py-4 bg-amber-500/10 rounded-lg">
                <p className="font-semibold mb-1">{opponentName} is OUT with {player1Score} runs!</p>
                <p className="text-sm text-emerald-300">Waiting for host to start your turn...</p>
              </div>
            )}

            {/* Waiting for opponent to play their turn */}
            {!isMyTurn && !isPlayerOut && roomStatus === 'playing' && (
              <div className="text-center text-emerald-300 py-4">
                <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                Waiting for {currentPlayerName} to play...
              </div>
            )}
          </div>

          {/* Ball History */}
          <div className="max-w-md mx-auto mt-6">
            <p className="text-emerald-300 text-sm mb-3 text-center">This Innings</p>
            <div className="flex flex-wrap justify-center gap-2">
              {currentBalls.map((ball, idx) => (
                <div
                  key={idx}
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold shadow-lg ${
                    ball === 0 || ball === 8
                      ? 'bg-gradient-to-br from-red-500 to-red-600'
                      : ball === 6
                        ? 'bg-gradient-to-br from-purple-500 to-purple-600'
                        : ball === 4
                          ? 'bg-gradient-to-br from-blue-500 to-blue-600'
                          : 'bg-gradient-to-br from-emerald-500 to-emerald-600'
                  }`}
                >
                  {ball === 0 || ball === 8 ? 'W' : ball}
                </div>
              ))}
              {currentBalls.length === 0 && (
                <p className="text-emerald-400 text-sm">No balls played yet</p>
              )}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="max-w-md mx-auto mt-4">
              <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 text-red-300 text-center text-sm">
                {error}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Lobby / Waiting state
  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-900 via-emerald-800 to-green-900 relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] border-4 border-white rounded-b-full" />
      </div>

      <div className="relative z-10 container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-white hover:bg-white/10"
          >
            <Home className="w-5 h-5 mr-2" />
            Home
          </Button>
          <div className="flex items-center gap-2">
            {isConnected ? (
              <Badge className="bg-emerald-500/80">
                <Wifi className="w-3 h-3 mr-1" /> Connected
              </Badge>
            ) : (
              <Badge className="bg-red-500/80">
                <WifiOff className="w-3 h-3 mr-1" /> Disconnected
              </Badge>
            )}
          </div>
        </div>

        <div className="max-w-md mx-auto space-y-6">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">Online Play</h1>
            <p className="text-emerald-300">Challenge your friends anywhere!</p>
          </div>

          {/* Connection status */}
          {isConnecting && (
            <Card className="bg-emerald-800/50 border-emerald-600/50">
              <CardContent className="p-6 text-center">
                <Loader2 className="w-8 h-8 text-emerald-400 animate-spin mx-auto mb-3" />
                <p className="text-white">Connecting to game server...</p>
              </CardContent>
            </Card>
          )}

          {/* Error */}
          {error && (
            <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 text-red-300 text-center">
              {error}
              <Button
                variant="link"
                onClick={() => setError(null)}
                className="text-red-300 underline ml-2"
              >
                Dismiss
              </Button>
            </div>
          )}

          {/* Idle state - Create or Join */}
          {roomStatus === 'idle' && isConnected && (
            <>
              {/* Player Name Input */}
              <Card className="bg-emerald-800/50 border-emerald-600/50">
                <CardContent className="p-6">
                  <label className="text-sm text-emerald-200 mb-2 block">Your Name</label>
                  <Input
                    placeholder="Enter your name..."
                    value={playerName}
                    onChange={(e) => setPlayerName(e.target.value)}
                    maxLength={20}
                    className="bg-emerald-900/50 border-emerald-600 text-white placeholder:text-emerald-400"
                  />
                </CardContent>
              </Card>

              {/* Create Room */}
              <Card className="bg-emerald-800/50 border-emerald-600/50">
                <CardHeader>
                  <CardTitle className="text-white">Create a Room</CardTitle>
                  <CardDescription className="text-emerald-300">
                    Start a new game and invite a friend
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    onClick={handleCreateRoom}
                    disabled={!playerName.trim()}
                    className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-bold py-6"
                  >
                    <Users className="w-5 h-5 mr-2" />
                    Create Room
                  </Button>
                </CardContent>
              </Card>

              {/* Join Room */}
              <Card className="bg-emerald-800/50 border-emerald-600/50">
                <CardHeader>
                  <CardTitle className="text-white">Join a Room</CardTitle>
                  <CardDescription className="text-emerald-300">
                    Enter the room code to join
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Input
                    placeholder="Enter room code..."
                    value={joinCode}
                    onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                    maxLength={6}
                    className="bg-emerald-900/50 border-emerald-600 text-white placeholder:text-emerald-400 text-center text-2xl tracking-widest"
                  />
                  <Button
                    onClick={handleJoinRoom}
                    disabled={!playerName.trim() || !joinCode.trim()}
                    className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold py-6"
                  >
                    Join Room
                  </Button>
                </CardContent>
              </Card>
            </>
          )}

          {/* Waiting for opponent */}
          {roomStatus === 'waiting' && (
            <Card className="bg-emerald-800/50 border-emerald-600/50">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-10 h-10 text-amber-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Waiting for Opponent</h3>
                <p className="text-emerald-300 mb-6">Share the room code with your friend</p>
                
                <div className="bg-emerald-900/50 rounded-lg p-4 mb-4">
                  <p className="text-emerald-400 text-sm mb-1">Room Code</p>
                  <p className="text-4xl font-bold text-white tracking-widest">{roomCode}</p>
                </div>
                
                <div className="flex gap-3">
                  <Button
                    onClick={handleCopyCode}
                    variant="outline"
                    className="flex-1 border-emerald-500 text-emerald-300 hover:bg-emerald-700/50"
                  >
                    {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                    {copied ? 'Copied!' : 'Copy Link'}
                  </Button>
                  <Button
                    onClick={handleShare}
                    className="flex-1 bg-amber-500 hover:bg-amber-600"
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    Share
                  </Button>
                </div>
                
                <Button
                  onClick={handleLeaveRoom}
                  variant="ghost"
                  className="mt-4 text-red-400 hover:text-red-300 hover:bg-red-500/20"
                >
                  Cancel
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Ready to start (host view) */}
          {roomStatus === 'ready' && isHost && (
            <Card className="bg-emerald-800/50 border-emerald-600/50">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-10 h-10 text-emerald-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Ready to Play!</h3>
                <p className="text-emerald-300 mb-6">
                  <span className="text-amber-400 font-semibold">{opponentName}</span> has joined
                </p>
                
                <Button
                  onClick={handleStartGame}
                  className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-bold py-6 text-lg"
                >
                  Start Match
                </Button>
                
                <Button
                  onClick={handleLeaveRoom}
                  variant="ghost"
                  className="mt-4 text-red-400 hover:text-red-300 hover:bg-red-500/20"
                >
                  Cancel
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Ready to start (guest view) */}
          {roomStatus === 'ready' && !isHost && (
            <Card className="bg-emerald-800/50 border-emerald-600/50">
              <CardContent className="p-8 text-center">
                <Loader2 className="w-12 h-12 text-emerald-400 animate-spin mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Joined!</h3>
                <p className="text-emerald-300 mb-4">
                  Waiting for <span className="text-amber-400 font-semibold">{opponentName}</span> to start the game
                </p>
                
                <Button
                  onClick={handleLeaveRoom}
                  variant="ghost"
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                >
                  Leave Room
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Share Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="bg-emerald-800 border-emerald-600 text-white">
          <DialogHeader>
            <DialogTitle>Share Room</DialogTitle>
            <DialogDescription className="text-emerald-300">
              Send this link to your friend to join the game
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-emerald-900/50 rounded-lg p-4 text-center">
              <p className="text-emerald-400 text-sm mb-1">Room Code</p>
              <p className="text-3xl font-bold text-white tracking-widest">{roomCode}</p>
            </div>
            <div className="flex gap-2">
              <Input
                value={`${window.location.origin}/online?room=${roomCode}`}
                readOnly
                className="bg-emerald-900 border-emerald-600 text-white text-sm"
              />
              <Button onClick={handleCopyCode} variant="outline" className="border-emerald-500 shrink-0">
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`Join my Book Cricket game! 🏏📖 Room: ${roomCode}\n${window.location.origin}/online?room=${roomCode}`)}`, '_blank')}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                WhatsApp
              </Button>
              <Button
                onClick={() => window.open(`sms:?body=${encodeURIComponent(`Join my Book Cricket game! Room: ${roomCode} - ${window.location.origin}/online?room=${roomCode}`)}`, '_blank')}
                className="flex-1 bg-blue-500 hover:bg-blue-600"
              >
                SMS
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OnlineGamePage;
